import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Compass, Users, Gauge, Check } from "lucide-react";
import { trackEvent } from "@/lib/api";

const HERO_IMG =
  "https://customer-assets-jai6qajn.emergentagent.net/job_map-finder-47/artifacts/1i4eomvd_brandagency-render%20%285%29.png";

const CALENDLY = "https://calendly.com/jocelyn-empresscoachingandconsulting/one-on-one-30-min";

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const offers = [
  {
    icon: Compass,
    name: "The Road Map",
    price: "$75 one time",
    intro: "Self-paced guidance to:",
    features: [
      "Get clear on what you actually want",
      "Turn it into milestones and next steps",
      "See what could get in the way before it stops you",
    ],
    cta: "Explore The Road Map",
    to: "/road-map",
  },
  {
    icon: Users,
    name: "The Road Trip",
    price: "$275/month",
    intro: "Monthly group coaching plus the full Road Trip program.",
    features: [
      "Practical tools and workbook exercises",
      "Two live group coaching calls each month",
      "Support, perspective, and accountability",
    ],
    cta: "Ask about The Road Trip",
    href: "mailto:jocelyn@empresscoachingandconsulting.com",
  },
  {
    icon: Gauge,
    name: "1:1 Coaching",
    price: "Private coaching",
    intro: "",
    features: [
      "Four private coaching calls each month",
      "Focused support around your decisions, patterns, and goals",
      "Coaching built around what you're actually working through",
    ],
    cta: "Book a free 30-minute discovery call",
    href: CALENDLY,
  },
];

export default function Landing() {
  useEffect(() => {
    trackEvent("landing_view", null);
  }, []);

  return (
    <div className="min-h-screen bg-[#EAE6E1] text-[#2A1B2E] overflow-x-hidden">
      <nav className="flex items-center justify-between px-6 md:px-12 py-6 max-w-7xl mx-auto">
        <Link to="/" data-testid="brand-logo" className="block h-12 w-48 overflow-hidden">
          <img
            src="https://customer-assets-jai6qajn.emergentagent.net/job_map-finder-47/artifacts/bl59sojl_499_logo-01.webp"
            alt="Empress Coaching and Consulting"
            className="h-full w-full object-cover object-center mix-blend-multiply"
          />
        </Link>
        <Link
          to="/quiz"
          data-testid="nav-quiz-btn"
          className="text-sm font-semibold uppercase tracking-[0.15em] border border-[#64246A] text-[#64246A] rounded-md px-5 py-2.5 hover:bg-[#64246A] hover:text-[#EAE6E1] transition-colors duration-300"
        >
          Take the quiz
        </Link>
      </nav>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-10 md:pt-16 pb-20 grid md:grid-cols-2 gap-12 md:gap-16 items-center">
        <div>
          <motion.p
            variants={fade} initial="hidden" animate="show" custom={0}
            className="text-sm uppercase tracking-[0.25em] font-semibold text-[#64246A] mb-6"
          >
            Lead from within. Create what matters.
          </motion.p>
          <motion.h1
            variants={fade} initial="hidden" animate="show" custom={1}
            className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
          >
            Nothing has to be wrong for you to want something different.
          </motion.h1>
          <motion.p
            variants={fade} initial="hidden" animate="show" custom={2}
            className="mt-6 text-lg text-[#2A1B2E]/75 max-w-md leading-relaxed"
          >
            Is your life perfectly fine, but you still have a sense that you'd like
            more? Maybe you know exactly what it is. Maybe you just know something
            feels off.
          </motion.p>
          <motion.p
            variants={fade} initial="hidden" animate="show" custom={3}
            className="mt-4 text-lg text-[#2A1B2E]/75 max-w-md leading-relaxed"
          >
            Most of us simply aren't sure what we need or where to start. Take this
            short quiz to get some insight.
          </motion.p>

          <motion.div
            variants={fade} initial="hidden" animate="show" custom={4}
            className="mt-9 flex flex-col sm:flex-row items-start sm:items-center gap-4"
          >
            <Link
              to="/quiz"
              data-testid="hero-start-quiz-btn"
              className="group inline-flex items-center gap-2 bg-[#64246A] text-white rounded-md px-8 py-4 font-semibold hover:bg-[#4A1A50] transition-colors duration-300"
            >
              Take the 2-minute quiz
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <a
              href={CALENDLY}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-call-btn"
              className="inline-flex items-center gap-2 border border-[#64246A] text-[#64246A] rounded-md px-8 py-4 font-semibold hover:bg-[#64246A] hover:text-white transition-colors duration-300"
            >
              Book a free 30-minute discovery call
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 1.03 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          <div className="rounded-lg overflow-hidden shadow-[0_20px_60px_rgba(100,36,106,0.28)]">
            <img
              src={HERO_IMG}
              alt="Jocelyn, founder of Empress Coaching"
              className="w-full h-[420px] md:h-[540px] object-cover object-top"
            />
          </div>
        </motion.div>
      </section>

      {/* Sound familiar? */}
      <section className="bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-24 text-center">
          <p className="text-sm uppercase tracking-[0.25em] font-semibold text-[#A87CA0] mb-6">
            Sound familiar?
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl tracking-tight leading-tight">
            You know you want something different. You just can't seem to get from here to there.
          </h2>
          <p className="mt-6 text-lg text-[#2A1B2E]/75 leading-relaxed">
            Maybe you're not sure what you want. Maybe you keep second-guessing yourself, losing momentum, or wondering where you'd find the time or energy to make a change.
          </p>
          <p className="mt-4 text-lg text-[#2A1B2E]/75 leading-relaxed">
            Take the 2-minute quiz to see what may be getting in the way and get a useful place to start.
          </p>
          <div className="mt-8 flex justify-center">
            <Link
              to="/quiz"
              data-testid="sound-familiar-quiz-btn"
              className="inline-flex items-center gap-2 bg-[#64246A] text-white rounded-md px-8 py-4 font-semibold hover:bg-[#4A1A50] transition-colors duration-300"
            >
              Take the 2-minute quiz <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Credibility */}
      <section className="max-w-3xl mx-auto px-6 md:px-12 pt-24 pb-6" data-testid="credibility-section">
        <p className="text-sm uppercase tracking-[0.25em] font-semibold text-[#A87CA0] mb-4">
          Meet Jocelyn
        </p>
        <h2 className="font-serif text-3xl sm:text-4xl tracking-tight">Hi, I'm Jocelyn.</h2>
        <p className="mt-5 text-lg text-[#2A1B2E]/75 leading-relaxed">
          I'm a Certified Professional Coach with more than 15 years leading teams,
          businesses, operations, and change. I know how easy it is to get very good
          at handling what's in front of you while losing sight of what you actually
          want.
        </p>
        <p className="mt-4 text-lg text-[#2A1B2E]/75 leading-relaxed">
          My coaching is warm, direct, and practical. I help you see what you can't
          see from inside your own thinking, challenge what may be keeping you stuck,
          and turn what matters into action.
        </p>
        <p className="mt-4 text-lg text-[#2A1B2E]/75 leading-relaxed">
          I won't hand you the answer. I'll help you get clear enough to trust your own.
        </p>
      </section>

      {/* Offers */}
      <section id="ways-to-work" className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <p className="text-sm uppercase tracking-[0.25em] font-semibold text-[#A87CA0] mb-4">
          Ways to work with me
        </p>
        <h2 className="font-serif text-3xl sm:text-4xl tracking-tight max-w-2xl">
          Choose the kind of support that fits what you need right now.
        </h2>
        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {offers.map((o, i) => (
            <motion.div
              key={o.name}
              variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
              className="h-full rounded-lg p-8 border bg-white border-[#2A1B2E]/10 flex flex-col"
              data-testid={`offer-card-${i}`}
            >
              <o.icon className="w-8 h-8 mb-6 text-[#A87CA0]" strokeWidth={1.5} />
              <h3 className="font-serif text-2xl">{o.name}</h3>
              <p className="mt-1 font-serif text-xl text-[#64246A]">{o.price}</p>
              {o.intro && <p className="mt-4 text-[#2A1B2E]/70 leading-relaxed">{o.intro}</p>}
              <ul className="mt-4 space-y-2.5 flex-1">
                {o.features.map((f, fi) => (
                  <li key={fi} className="flex gap-2.5 text-sm text-[#2A1B2E]/80">
                    <Check className="w-4 h-4 text-[#A87CA0] shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              {o.to ? (
                <Link
                  to={o.to}
                  data-testid={`offer-cta-${i}`}
                  className="mt-7 inline-flex items-center justify-center gap-2 rounded-md px-6 py-3.5 font-semibold bg-[#64246A] text-white hover:bg-[#4A1A50] transition-colors duration-300"
                >
                  {o.cta} <ArrowRight className="w-4 h-4" />
                </Link>
              ) : (
                <a
                  href={o.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`offer-cta-${i}`}
                  className="mt-7 inline-flex items-center justify-center gap-2 rounded-md px-6 py-3.5 font-semibold bg-[#64246A] text-white hover:bg-[#4A1A50] transition-colors duration-300 text-center"
                >
                  {o.cta} <ArrowRight className="w-4 h-4 shrink-0" />
                </a>
              )}
            </motion.div>
          ))}
        </div>
      </section>

      {/* Bottom quiz CTA */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24">
        <div className="rounded-lg bg-[#64246A] text-[#EAE6E1] px-8 md:px-16 py-16 md:py-20">
          <p className="text-sm uppercase tracking-[0.25em] font-semibold text-[#EAE6E1]/70 mb-4">
            Not sure where to start?
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl leading-tight max-w-2xl">
            Take the 2-minute quiz and get a clearer next step.
          </h2>
          <Link
            to="/quiz"
            data-testid="cta-band-quiz-btn"
            className="mt-8 inline-flex items-center gap-2 bg-[#EAE6E1] text-[#64246A] rounded-md px-8 py-4 font-semibold hover:bg-white transition-colors duration-300"
          >
            Take the 2-minute quiz <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <footer className="border-t border-[#2A1B2E]/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-[#2A1B2E]/60">
          <span className="font-serif text-base text-[#2A1B2E]">Empress Coaching &amp; Consulting</span>
          <span>Lead from within. Create what matters.</span>
        </div>
      </footer>
    </div>
  );
}
